import type { InstrumentType } from '@/domain/types';
import type { AutomatableParam } from '../effects/EffectNode';
import { defaultInstrumentParams, padForPitch } from './descriptors';
import { noiseBuffer, rampParam, registerInstrument, type InstrumentNode } from './Instrument';

/**
 * A synthesised drum kit.
 *
 * Every voice is built from oscillators and filtered noise at the moment it is
 * scheduled, then thrown away. No samples means no download, no licensing, and
 * a kit that works offline on first load — and percussion is one of the few
 * places where synthesis from first principles still sounds convincing.
 *
 * Pads are addressed by MIDI note number, so a drum pattern is an ordinary MIDI
 * clip. The step sequencer and the piano roll are two views of that same clip
 * rather than two data models that could disagree.
 */

interface Scheduled {
  nodes: AudioNode[];
  endsAt: number;
  /**
   * For a hit another hit can cut short — a hat — its envelope as scheduled,
   * so a cut can start from the level the decay will have reached:
   * everything here is scheduled ahead, and "now" is not when it sounds.
   */
  choke?: { gain: GainNode; source: AudioScheduledSourceNode; level: number; startsAt: number; decaysTo: number };
}

/** How long a choked hat takes to go: a cut, not a click. */
const CHOKE_FADE_SEC = 0.005;
/** The floor the noise hits decay to; a choke ramps to it as well. */
const DECAY_FLOOR = 0.0001;

class DrumMachine implements InstrumentNode {
  readonly type: InstrumentType = 'drumMachine';
  readonly output: GainNode;

  private readonly tone: BiquadFilterNode;
  private params: Record<string, number>;
  private scheduled: Scheduled[] = [];

  constructor(private readonly ctx: BaseAudioContext) {
    this.params = defaultInstrumentParams('drumMachine');
    this.output = ctx.createGain();
    this.output.gain.value = this.params.volume;

    this.tone = ctx.createBiquadFilter();
    this.tone.type = 'lowpass';
    this.tone.frequency.value = this.params.tone;
    this.tone.connect(this.output);
  }

  setParam(paramId: string, value: number, atTime?: number): void {
    this.params[paramId] = value;
    if (paramId === 'volume') rampParam(this.output.gain, value, this.ctx, atTime);
    else if (paramId === 'tone') rampParam(this.tone.frequency, value, this.ctx, atTime);
    // Per-pad settings are read when a hit is scheduled.
  }

  paramTargets(paramId: string): AutomatableParam[] {
    const identity = (v: number): number => v;
    if (paramId === 'volume') return [{ param: this.output.gain, map: identity }];
    if (paramId === 'tone') return [{ param: this.tone.frequency, map: identity }];
    return [];
  }

  setState(): void {
    // This instrument keeps no structured state beyond its parameters.
  }

  playNote(pitch: number, velocity: number, when: number): void {
    this.trigger(pitch, velocity, when);
  }

  noteOn(pitch: number, velocity: number, when: number): void {
    this.trigger(pitch, velocity, when);
  }

  noteOff(): void {
    // Percussion voices are one-shots: they ring for their own decay time and
    // ignore note-off, which is what a drum does.
  }

  allNotesOff(when: number): void {
    for (const item of [...this.scheduled]) {
      if (item.endsAt > when) this.release(item);
    }
  }

  private trigger(pitch: number, velocity: number, when: number): void {
    const pad = padForPitch(pitch);
    if (!pad) return;

    this.reap(when);

    const level = (this.params[`${pad.id}Level`] ?? 0.85) * (Math.max(1, Math.min(127, velocity)) / 127);
    const tune = this.params[`${pad.id}Tune`] ?? 0;
    const decay = this.params[`${pad.id}Decay`] ?? 0.3;
    const semitone = Math.pow(2, tune / 12);

    switch (pad.id) {
      case 'kick':
        this.tonalHit(when, level, 120 * semitone, 46 * semitone, decay, 0.045, 'sine');
        break;
      case 'tom':
        this.tonalHit(when, level * 0.9, 260 * semitone, 110 * semitone, decay, 0.09, 'sine');
        break;
      case 'snare':
        this.tonalHit(when, level * 0.5, 240 * semitone, 180 * semitone, decay * 0.5, 0.03, 'triangle');
        this.noiseHit(when, level * 0.9, decay, 1600 * semitone, 'highpass');
        break;
      case 'clap':
        // Three short bursts a few milliseconds apart: what makes a clap read
        // as many hands rather than one.
        for (let i = 0; i < 3; i++) {
          this.noiseHit(when + i * 0.011, level * (i === 2 ? 1 : 0.55), i === 2 ? decay : 0.035, 1100 * semitone, 'bandpass', 2.5);
        }
        break;
      case 'hatClosed':
        // The closed hat is the open one shut: on a kit they are one cymbal.
        this.chokeHats(when);
        this.noiseHit(when, level * 0.7, Math.min(decay, 0.12), 7000 * semitone, 'highpass', 0.9, true);
        break;
      case 'hatOpen':
        this.noiseHit(when, level * 0.6, Math.max(decay, 0.35), 6500 * semitone, 'highpass', 0.9, true);
        break;
      default:
        this.tonalHit(when, level * 0.7, 900 * semitone, 500 * semitone, Math.min(decay, 0.16), 0.02, 'square');
        break;
    }
  }

  /** A pitched hit: an oscillator swept down, with an exponential amplitude decay. */
  private tonalHit(
    when: number,
    level: number,
    startFreq: number,
    endFreq: number,
    decay: number,
    pitchTime: number,
    wave: OscillatorType,
  ): void {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = wave;
    osc.connect(gain).connect(this.tone);

    const end = when + decay;
    osc.frequency.setValueAtTime(Math.max(20, startFreq), when);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, endFreq), when + Math.max(0.005, pitchTime));

    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(level, when + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.start(when);
    osc.stop(end + 0.01);
    this.track([osc, gain], end + 0.01, osc);
  }

  /**
   * Cuts every hat still ringing at `when` — the open one that the closed
   * one shuts, and an earlier closed one, as a hi-hat does — from the level
   * its decay will have reached by then. The decay is exponential from the peak a
   * millisecond in to the floor at its end, so the level at any moment is
   * arithmetic; the cut is scheduled on the same timeline as the hit was,
   * which is what lets a render — where every hit is scheduled before any
   * sounds — cut it exactly where the closed hat lands.
   */
  private chokeHats(when: number): void {
    for (const item of this.scheduled) {
      const choke = item.choke;
      if (!choke || item.endsAt <= when || choke.startsAt > when) continue;
      const rise = choke.startsAt + 0.001;
      const span = Math.max(0.001, choke.decaysTo - rise);
      const progress = Math.min(1, Math.max(0, (when - rise) / span));
      const levelNow = choke.level * Math.pow(DECAY_FLOOR / Math.max(DECAY_FLOOR, choke.level), progress);
      try {
        // Not a cancel: cancelling the decay's ramp would take the whole ramp
        // with it, and the hat would sit at its peak until the cut. A ramp
        // *ending* at the cut, at the level the decay reaches there, is the
        // same curve as the decay up to that moment; the cut follows it, and
        // the decay's own end event is then a ramp from the floor to the floor.
        choke.gain.gain.exponentialRampToValueAtTime(Math.max(DECAY_FLOOR, levelNow), when);
        choke.gain.gain.exponentialRampToValueAtTime(DECAY_FLOOR, when + CHOKE_FADE_SEC);
        choke.source.stop(when + CHOKE_FADE_SEC + 0.001);
      } catch {
        /* already stopped */
      }
      item.endsAt = Math.min(item.endsAt, when + CHOKE_FADE_SEC + 0.001);
    }
  }

  /** A noise hit through a filter: the basis of snares, hats and claps. */
  private noiseHit(
    when: number,
    level: number,
    decay: number,
    frequency: number,
    filterType: BiquadFilterType,
    q = 0.9,
    /** A hat: a hit a later closed hat cuts short. */
    chokeable = false,
  ): void {
    const ctx = this.ctx;
    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer(ctx);
    // A random offset into the shared noise buffer stops repeated hits sounding
    // identical, which is what gives a machine-gun hi-hat away.
    source.loop = true;
    const offset = Math.random() * 0.5;

    const filter = ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = Math.max(40, frequency);
    filter.Q.value = q;

    const gain = ctx.createGain();
    source.connect(filter).connect(gain).connect(this.tone);

    const end = when + Math.max(0.01, decay);
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(level, when + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    source.start(when, offset);
    source.stop(end + 0.01);
    const item = this.track([source, filter, gain], end + 0.01, source);
    if (chokeable) item.choke = { gain, source, level, startsAt: when, decaysTo: end };
  }

  private track(nodes: AudioNode[], endsAt: number, source: AudioScheduledSourceNode): Scheduled {
    const item: Scheduled = { nodes, endsAt };
    this.scheduled.push(item);
    source.onended = () => this.dispose_(item);
    return item;
  }

  private release(item: Scheduled): void {
    for (const node of item.nodes) {
      const source = node as Partial<AudioScheduledSourceNode>;
      if (typeof source.stop === 'function') {
        try {
          source.stop();
        } catch {
          /* already stopped */
        }
      }
    }
  }

  private dispose_(item: Scheduled): void {
    const index = this.scheduled.indexOf(item);
    if (index >= 0) this.scheduled.splice(index, 1);
    for (const node of item.nodes) {
      try {
        node.disconnect();
      } catch {
        /* already disconnected */
      }
    }
  }

  /**
   * Forgets hits that will have finished by `now`.
   *
   * As in the synth, this must not disconnect anything: an offline render
   * schedules every hit before any of them has produced a sample, so tearing
   * down a bar 1 kick while scheduling a bar 4 hat would delete the kick from
   * the file. `onended` and `dispose()` do the actual disconnection.
   */
  private reap(now: number): void {
    for (let i = this.scheduled.length - 1; i >= 0; i--) {
      if (this.scheduled[i].endsAt < now) this.scheduled.splice(i, 1);
    }
  }

  dispose(): void {
    for (const item of [...this.scheduled]) this.dispose_(item);
    this.tone.disconnect();
    this.output.disconnect();
  }
}

registerInstrument('drumMachine', (ctx) => new DrumMachine(ctx));
